import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, DatePicker, Statistic,
} from 'antd'
import { TeamOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const SHIFTS = ['A', 'B', 'C']

export default function ContractorLaborPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [dateFilter, setDateFilter] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const [contractorFilter, setContractorFilter] = useState<string | undefined>(undefined)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: contractors } = useQuery({
    queryKey: ['contractors', plantId],
    queryFn: () =>
      apiClient.get(`${API}/contractors`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: laborData, isLoading } = useQuery({
    queryKey: ['contractor-labor', plantId, dateFilter, contractorFilter],
    queryFn: () =>
      apiClient.get(`${API}/contractor-labor`, {
        params: {
          plant_id: plantId,
          date: dateFilter || undefined,
          contractor_id: contractorFilter || undefined,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/contractor-labor`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractor-labor'] })
      message.success('Labor entry created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const exitMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/contractor-labor/${id}/exit`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractor-labor'] })
      message.success('Exit recorded')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(laborData) ? laborData : []
  const insideCount = list.filter((l: any) => l.status === 'INSIDE').length
  const exitedCount = list.filter((l: any) => l.status === 'EXITED').length

  const contractorList = Array.isArray(contractors) ? contractors : []

  const columns = [
    { title: 'Labor Name', dataIndex: 'labor_name', key: 'labor_name' },
    { title: 'ID No', dataIndex: 'labor_id_no', key: 'labor_id_no', width: 120 },
    { title: 'Work Zone', dataIndex: 'work_zone', key: 'work_zone', width: 130 },
    {
      title: 'In Time',
      dataIndex: 'in_time',
      key: 'in_time',
      width: 140,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Out Time',
      dataIndex: 'out_time',
      key: 'out_time',
      width: 140,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'INSIDE' ? 'green' : 'default'}>{v}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, r: any) =>
        r.status === 'INSIDE' ? (
          <Button
            size="small"
            danger
            loading={exitMut.isPending}
            onClick={() => exitMut.mutate(r.id)}
          >
            Record Exit
          </Button>
        ) : null,
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <TeamOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Contractor Labor
      </Title>
      <Typography.Text type="secondary">Daily contractor labor tracking</Typography.Text>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Currently Inside" value={insideCount} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Exited Today" value={exitedCount} valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Space wrap>
              <Select
                value={plantId}
                onChange={setPlantId}
                style={{ width: 200 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
              />
              <DatePicker
                value={dateFilter ? dayjs(dateFilter) : null}
                onChange={d => setDateFilter(d ? d.format('YYYY-MM-DD') : '')}
                allowClear
              />
              <Select
                allowClear
                placeholder="Filter by Contractor"
                style={{ width: 200 }}
                value={contractorFilter}
                onChange={v => setContractorFilter(v)}
                options={contractorList.map((c: any) => ({
                  value: c.id,
                  label: c.contractor_name,
                }))}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); form.setFieldValue('plant_id', plantId); setDrawerOpen(true) }}
            >
              Record Entry
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Drawer
        title="Record Labor Entry"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals =>
                    createMut.mutate({ ...vals, plant_id: plantId })
                  )
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="contractor_id" label="Contractor" rules={[{ required: true }]}>
            <Select
              options={contractorList.map((c: any) => ({
                value: c.id,
                label: c.contractor_name,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="labor_name" label="Labor Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="labor_id_no" label="Labor ID No">
            <Input />
          </Form.Item>
          <Form.Item name="work_zone" label="Work Zone">
            <Input placeholder="e.g. Spinning Hall" />
          </Form.Item>
          <Form.Item name="shift" label="Shift">
            <Select options={SHIFTS.map(s => ({ value: s, label: `Shift ${s}` }))} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
